import React, { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { Search, Loader2 } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import CosmicBackground from '@/components/CosmicBackground';
import { Navbar } from '@/components/Layout/Navbar';
import { Footer } from '@/components/Layout/Footer';
import { BlogCard } from '@/components/Blog/BlogCard';
import { CategoryPills } from '@/components/Blog/CategoryPills';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const CATEGORIES = ['All', 'AI News', 'Tutorials', 'Creator Stories', 'Product Updates', 'GEO Insights'];
const POSTS_PER_PAGE = 6;

interface BlogPost {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  featured_image: string | null;
  category: string;
  author_id: string | null;
  reading_time_minutes: number;
  published_at: string | null;
  tags: string[] | null;
  profiles?: {
    username: string;
    avatar_id: number | null;
  } | null;
}

const Blog = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [visiblePosts, setVisiblePosts] = useState(POSTS_PER_PAGE);

  const { data: posts, isLoading } = useQuery({
    queryKey: ['blog-posts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('blog_posts')
        .select(`
          id,
          slug,
          title,
          excerpt,
          featured_image,
          category,
          author_id,
          reading_time_minutes,
          published_at,
          tags,
          profiles:author_id (
            username,
            avatar_id
          )
        `)
        .eq('is_published', true)
        .order('published_at', { ascending: false });

      if (error) throw error;
      return data as BlogPost[];
    },
  });

  const filteredPosts = useMemo(() => {
    if (!posts) return [];
    
    return posts.filter((post) => {
      const matchesSearch = searchQuery === '' || 
        post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesCategory = activeCategory === 'All' || 
        post.category.toLowerCase() === activeCategory.toLowerCase();
      
      return matchesSearch && matchesCategory;
    });
  }, [posts, searchQuery, activeCategory]);

  const displayedPosts = filteredPosts.slice(0, visiblePosts);
  const hasMore = visiblePosts < filteredPosts.length;

  const loadMore = () => {
    setVisiblePosts((prev) => prev + POSTS_PER_PAGE);
  };

  const blogListSchema = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "ThePromptSpace Blog",
    "description": "Insights, tutorials, and updates from ThePromptSpace - the future of GenAI creation.",
    "url": "https://thepromptspace.com/blog",
    "publisher": {
      "@type": "Organization",
      "name": "ThePromptSpace",
      "logo": {
        "@type": "ImageObject",
        "url": "https://thepromptspace.com/logo.png"
      }
    }
  };

  return (
    <>
      <Helmet>
        <title>Blog - ThePromptSpace | AI Prompts, Tutorials & Creator Stories</title>
        <meta name="description" content="Discover insights, tutorials, and updates from ThePromptSpace. Learn about AI prompts, GEO optimization, and connect with the creator community." />
        <meta property="og:title" content="Blog - ThePromptSpace" />
        <meta property="og:description" content="Discover insights, tutorials, and updates from ThePromptSpace." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://thepromptspace.com/blog" />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="canonical" href="https://thepromptspace.com/blog" />
        <script type="application/ld+json">
          {JSON.stringify(blogListSchema)}
        </script>
      </Helmet>

      <CosmicBackground />
      <Navbar />

      <main className="min-h-screen pt-24 pb-16">
        {/* Hero Section */}
        <header className="text-center mb-12 px-4">
          <h1 className="text-3xl md:text-4xl lg:text-6xl font-bold font-display mb-6 bg-gradient-to-r from-[#00C2FF] via-[#7000FF] to-[#FF00A8] bg-clip-text text-transparent">
            Insights & Updates
          </h1>
          <p className="text-white/70 text-base md:text-lg lg:text-xl max-w-2xl mx-auto mb-8">
            Explore the latest in AI prompts, creator stories, and platform updates
          </p>
          
          {/* Search Bar */}
          <div className="max-w-xl mx-auto relative px-2 sm:px-0">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
            <Input
              type="text"
              placeholder="Search articles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 h-12 bg-white/10 border border-[#00C2FF]/30 rounded-xl text-white placeholder:text-white/40 focus:border-[#00C2FF] focus:ring-[#00C2FF]/20 backdrop-blur-md"
            />
          </div>
        </header>

        {/* Category Filter */}
        <nav className="max-w-7xl mx-auto px-4 mb-10" aria-label="Blog categories">
          <CategoryPills
            categories={CATEGORIES}
            activeCategory={activeCategory}
            onCategoryChange={setActiveCategory}
          />
        </nav>

        {/* Blog Grid */}
        <div className="max-w-7xl mx-auto px-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-[#00C2FF]" />
            </div>
          ) : displayedPosts.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-white/60 text-lg">No articles found</p>
              <p className="text-white/40 mt-2">Try adjusting your search or category filter</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
              {displayedPosts.map((post) => (
                <article key={post.id}>
                  <BlogCard post={post} />
                </article>
              ))}
            </div>
          )}

          {/* Load More Button */}
          {hasMore && (
            <div className="flex justify-center mt-12">
              <Button
                onClick={loadMore}
                className="px-8 py-3 bg-gradient-to-r from-[#7000FF] to-[#FF00A8] hover:opacity-90 text-white font-medium rounded-xl shadow-[0_0_30px_rgba(112,0,255,0.3)] hover:shadow-[0_0_40px_rgba(112,0,255,0.5)] transition-all duration-300"
              >
                Load More Articles
              </Button>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
};

export default Blog;
